import React from "react";
import { Link } from "react-router-dom";
import { asset, assetSet } from "@/lib/asset";
import { ExternalLink, Check } from "lucide-react";
import { SERVICES } from "@/data/site";

const SPANS = {
  visite: "md:col-span-7",
  photos: "md:col-span-5",
  leads: "md:col-span-12",
};

// Route de destination pour chaque service (clic sur toute la card)
const SERVICE_HREFS = {
  visite: "/nos-realisations",
  photos: "/portfolio",
};

// Visuels de fond (décoratifs) — cadrage adapté à chaque sujet
const BACKGROUNDS = {
  visite: {
    src: asset("/services/visite-900.webp"),
    srcSet: assetSet("/services/visite-480.webp 480w, /services/visite-640.webp 640w, /services/visite-900.webp 900w, /services/visite-1600.webp 1600w"),
    sizes: "(min-width: 1280px) 960px, (min-width: 768px) 58vw, 100vw",
    position: "object-[62%_72%] md:object-[center_68%]",
  },
  photos: {
    src: asset("/services/photos-900.webp"),
    srcSet: assetSet("/services/photos-480.webp 480w, /services/photos-640.webp 640w, /services/photos-900.webp 900w, /services/photos-1600.webp 1600w"),
    sizes: "(min-width: 1280px) 680px, (min-width: 768px) 42vw, 100vw",
    position: "object-[58%_58%] md:object-[center_60%]",
  },
  leads: {
    src: asset("/services/leads-900.webp"),
    srcSet: assetSet("/services/leads-480.webp 480w, /services/leads-640.webp 640w, /services/leads-900.webp 900w, /services/leads-1600.webp 1600w"),
    sizes: "(min-width: 1280px) 1650px, 100vw",
    position: "object-[66%_40%] md:object-[center_42%]",
  },
};

const reveal = {};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="vp-section section-glow relative">
      <div className="vp-shell px-6 md:px-10">
        <div className="mb-10 md:mb-14 max-w-2xl vp-fade-rise">
          <div className="overline mb-4">Nos services</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight leading-[1.1]">
            Une solution <span className="accent-red">complète</span> pour valoriser vos espaces.
          </h2>
          <p className="mt-4 text-zinc-400 leading-relaxed">
            Captation immersive, visuels professionnels et qualification de vos prospects :
            trois prestations qui fonctionnent ensemble.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          {SERVICES.map((s, idx) => {
            const isHero = s.key === "visite";
            const isWide = s.key === "leads";
            const isExternal = Boolean(s.external);
            const href = isExternal ? s.external : SERVICE_HREFS[s.key] || "/";
            const Card = isExternal ? "a" : Link;
            const bg = BACKGROUNDS[s.key];
            const linkProps = isExternal
              ? { href, target: "_blank", rel: "noopener noreferrer" }
              : { to: href };
            return (
              <Card
                key={s.key}
                {...linkProps}
                style={{ animationDelay: `${idx * 60}ms` }}
                aria-label={`Découvrir ${s.title}`}
                className={`surface-card overflow-hidden rounded-2xl flex flex-col cursor-pointer group vp-fade-rise ${SPANS[s.key]} ${
                  isHero ? "border-[#E50914]/25" : ""
                }`}
                data-testid={`service-${s.key}`}
              >
                {bg && (
                  <div className={`relative overflow-hidden ${isWide ? "h-52 md:h-72" : "h-52 md:h-64"}`}>
                    <img
                      src={bg.src}
                      srcSet={bg.srcSet}
                      sizes={bg.sizes}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      decoding="async"
                      className={`absolute inset-0 w-full h-full object-cover ${bg.position} transition-transform duration-700 ease-out group-hover:scale-105 motion-reduce:transform-none motion-reduce:transition-none`}
                      data-testid={`service-bg-${s.key}`}
                    />
                    <span aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" />
                  </div>
                )}

                <div className="p-7 md:p-9 flex flex-col flex-grow">
                  <div className={isWide ? "grid gap-8 xl:grid-cols-[minmax(0,1fr)_22rem]" : ""}>
                    <div>
                      <div className="overline mb-2">{s.subtitle}</div>
                      <h3
                        className={`font-display font-semibold tracking-tight mb-3 ${
                          isHero ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"
                        }`}
                      >
                        {s.title}
                      </h3>
                      <div className="space-y-3 vp-measure">
                        {s.paras.map((p) => (
                          <p key={p.slice(0, 24)} className="text-sm text-zinc-400 leading-relaxed">
                            {p}
                          </p>
                        ))}
                      </div>
                    </div>

                    <ul className={`mt-6 space-y-3 ${isWide ? "xl:mt-0" : ""}`}>
                      {s.highlights.map((h) => (
                        <li key={h} className="flex items-start gap-2.5">
                          <Check className="w-4 h-4 text-[#E50914] mt-0.5 flex-shrink-0" />
                          <span className="text-sm leading-snug text-zinc-300">{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {s.cta && (
                    <span
                      data-testid={`service-cta-${s.key}`}
                      className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#FF4D6D] group-hover:text-[#E50914] transition-colors"
                    >
                      {s.cta} <ExternalLink className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}
